import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  Alert, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { ExpandMore, CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface TestResult {
  module: string;
  endpoint: string;
  status: 'success' | 'error' | 'loading' | 'warning';
  data?: any;
  error?: string;
  responseTime?: number;
  timestamp: Date;
}

interface ModuleTest {
  name: string;
  description: string;
  endpoints: Array<{
    name: string;
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    expectedStatus?: number;
  }>;
}

const E2EIntegrationTest: React.FC = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [overallStatus, setOverallStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>('idle');
  const [summary, setSummary] = useState({
    total: 0,
    success: 0,
    error: 0,
    warning: 0,
    avgResponseTime: 0
  });

  // Определяем все модули для тестирования
  const modules: ModuleTest[] = [
    {
      name: '🔐 Аутентификация',
      description: 'Тестирование системы аутентификации и авторизации',
      endpoints: [
        { name: 'Health Check', url: '/health', method: 'GET', expectedStatus: 200 },
        { name: 'User Profile', url: '/auth/me', method: 'GET', expectedStatus: 200 },
        { name: 'Token Validation', url: '/auth/validate', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '📊 Analytics',
      description: 'Тестирование аналитических модулей и дашбордов',
      endpoints: [
        { name: 'Analytics Dashboards', url: '/analytics/dashboards', method: 'GET', expectedStatus: 200 },
        { name: 'Analytics Widgets', url: '/analytics/widgets', method: 'GET', expectedStatus: 200 },
        { name: 'Analytics Metrics', url: '/analytics/metrics', method: 'GET', expectedStatus: 200 },
        { name: 'Analytics Reports', url: '/analytics/reports', method: 'GET', expectedStatus: 200 },
        { name: 'Analytics Predictions', url: '/analytics/predictions', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '🛒 Marketplace',
      description: 'Тестирование маркетплейса поставщиков и услуг',
      endpoints: [
        { name: 'Marketplace Suppliers', url: '/api/marketplace/suppliers', method: 'GET', expectedStatus: 200 },
        { name: 'Marketplace Services', url: '/api/marketplace/services', method: 'GET', expectedStatus: 200 },
        { name: 'Marketplace Quotations', url: '/api/marketplace/quotations', method: 'GET', expectedStatus: 200 },
        { name: 'Marketplace Orders', url: '/api/marketplace/orders', method: 'GET', expectedStatus: 200 },
        { name: 'Marketplace Reviews', url: '/api/marketplace/reviews', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '👥 Teams',
      description: 'Тестирование системы команд и участников',
      endpoints: [
        { name: 'Teams List', url: '/teams', method: 'GET', expectedStatus: 200 },
        { name: 'Team Members', url: '/teams/1/members', method: 'GET', expectedStatus: 200 },
        { name: 'Team Stats', url: '/teams/1/stats', method: 'GET', expectedStatus: 200 },
        { name: 'Team Invites', url: '/teams/1/invites', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '🎯 Referrals',
      description: 'Тестирование реферальной системы',
      endpoints: [
        { name: 'Referrals Stats', url: '/referrals/stats', method: 'GET', expectedStatus: 200 },
        { name: 'Referrals Invites', url: '/referrals/invites', method: 'GET', expectedStatus: 200 },
        { name: 'Referrals Earnings', url: '/referrals/earnings', method: 'GET', expectedStatus: 200 },
        { name: 'Referrals Analytics', url: '/referrals/analytics', method: 'GET', expectedStatus: 200 },
        { name: 'Referrals Leaderboard', url: '/referrals/leaderboard', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '🤖 AI Support',
      description: 'Тестирование AI системы поддержки',
      endpoints: [
        { name: 'AI Support Health', url: '/ai-support/health', method: 'GET', expectedStatus: 200 },
        { name: 'AI Support Config', url: '/ai-support/config', method: 'GET', expectedStatus: 200 },
        { name: 'AI Support Operators', url: '/ai-support/operators/available', method: 'GET', expectedStatus: 200 },
        { name: 'AI Knowledge Base', url: '/ai-support/knowledge-base/categories', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '📄 Documents',
      description: 'Тестирование системы документов',
      endpoints: [
        { name: 'Documents Contracts', url: '/documents/contracts', method: 'GET', expectedStatus: 200 },
        { name: 'Documents Invoices', url: '/documents/invoices', method: 'GET', expectedStatus: 200 },
        { name: 'Documents Reports', url: '/documents/reports', method: 'GET', expectedStatus: 200 }
      ]
    },
    {
      name: '💰 Finance',
      description: 'Тестирование финансовых модулей',
      endpoints: [
        { name: 'Finance Budget', url: '/finance/budget', method: 'GET', expectedStatus: 200 },
        { name: 'Finance Transactions', url: '/finance/transactions', method: 'GET', expectedStatus: 200 },
        { name: 'Finance Analytics', url: '/finance/analytics', method: 'GET', expectedStatus: 200 }
      ]
    }
  ];

  const testEndpoint = async (module: string, endpoint: { name: string; url: string; method: string; expectedStatus?: number }) => {
    const startTime = Date.now();
    const result: TestResult = {
      module,
      endpoint: endpoint.name,
      status: 'loading',
      timestamp: new Date()
    };

    setResults(prev => [...prev, result]);

    try {
      const response = await api.get(endpoint.url);
      const responseTime = Date.now() - startTime;
      
      const status = response.status === endpoint.expectedStatus ? 'success' : 'warning';
      
      setResults(prev => prev.map(r => 
        r.module === module && r.endpoint === endpoint.name
          ? { 
              ...result, 
              status, 
              data: response.data, 
              responseTime,
              timestamp: new Date()
            }
          : r
      ));
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      setResults(prev => prev.map(r => 
        r.module === module && r.endpoint === endpoint.name
          ? { 
              ...result, 
              status: 'error', 
              error: error.response?.data?.message || error.message,
              responseTime,
              timestamp: new Date()
            }
          : r
      ));
    }
  };

  const testAllModules = async () => {
    setIsLoading(true);
    setOverallStatus('running');
    setResults([]);

    for (const module of modules) {
      for (const endpoint of module.endpoints) {
        await testEndpoint(module.name, endpoint);
        // Небольшая задержка между запросами
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    setIsLoading(false);
    setOverallStatus('completed');
    calculateSummary();
  };

  const calculateSummary = () => {
    const total = results.length;
    const success = results.filter(r => r.status === 'success').length;
    const error = results.filter(r => r.status === 'error').length;
    const warning = results.filter(r => r.status === 'warning').length;
    const avgResponseTime = results.reduce((sum, r) => sum + (r.responseTime || 0), 0) / total;

    setSummary({ total, success, error, warning, avgResponseTime });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle color="success" />;
      case 'error': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'loading': return <CircularProgress size={16} />;
      default: return <Info color="info" />;
    }
  };

  const _getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success.main';
      case 'error': return 'error.main';
      case 'warning': return 'warning.main';
      case 'loading': return 'info.main';
      default: return 'text.secondary';
    }
  };

  const getModuleResults = (moduleName: string) => {
    return results.filter(r => r.module === moduleName);
  };

  const getModuleStatus = (moduleName: string) => {
    const moduleResults = getModuleResults(moduleName);
    if (moduleResults.length === 0) return 'idle';
    if (moduleResults.some(r => r.status === 'error')) return 'error';
    if (moduleResults.some(r => r.status === 'warning')) return 'warning';
    if (moduleResults.every(r => r.status === 'success')) return 'success';
    return 'loading';
  };

  return (
    <Card sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        🧪 E2E Тестирование интеграции с Backend API
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Пользователь: {user?.email} (ID: {user?.id})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Backend URL: {process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Всего модулей для тестирования: {modules.length}
        </Typography>
      </Box>

      {/* Сводка результатов */}
      {overallStatus === 'completed' && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            📊 Сводка тестирования
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">{summary.total}</Typography>
                <Typography variant="body2">Всего тестов</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">{summary.success}</Typography>
                <Typography variant="body2">Успешно</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main">{summary.error}</Typography>
                <Typography variant="body2">Ошибки</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">{summary.warning}</Typography>
                <Typography variant="body2">Предупреждения</Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Среднее время ответа: {summary.avgResponseTime.toFixed(0)}ms
            </Typography>
          </Box>
        </Paper>
      )}

      <Button 
        variant="contained" 
        size="large"
        onClick={testAllModules}
        disabled={isLoading}
        sx={{ mb: 3 }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Тестирование...
          </>
        ) : (
          '🚀 Запустить полное E2E тестирование'
        )}
      </Button>

      {/* Результаты по модулям */}
      {modules.map((module) => {
        const moduleResults = getModuleResults(module.name);
        const moduleStatus = getModuleStatus(module.name);
        
        return (
          <Accordion key={module.name} sx={{ mb: 1, color: _getStatusColor(moduleStatus) }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {getStatusIcon(moduleStatus)}
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {module.name}
                </Typography>
                <Chip 
                  label={`${moduleResults.filter(r => r.status === 'success').length}/${module.endpoints.length}`}
                  color={moduleStatus === 'success' ? 'success' : moduleStatus === 'error' ? 'error' : 'default'}
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {module.description}
              </Typography>
              
              <List dense>
                {module.endpoints.map((endpoint) => {
                  const result = moduleResults.find(r => r.endpoint === endpoint.name);
                  
                  return (
                    <ListItem key={endpoint.name} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {result ? getStatusIcon(result.status) : <Info color="disabled" />}
                            <Typography variant="body2">
                              {endpoint.method} {endpoint.name}
                            </Typography>
                            {result?.responseTime && (
                              <Chip 
                                label={`${result.responseTime}ms`} 
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          result?.status === 'error' ? (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {result.error}
                            </Alert>
                          ) : result?.status === 'warning' ? (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              Неожиданный статус ответа
                            </Alert>
                          ) : result?.status === 'success' ? (
                            <Alert severity="success" sx={{ mt: 1 }}>
                              Успешно! Данные получены
                            </Alert>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Ожидает тестирования
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {results.length === 0 && !isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Нажмите кнопку для начала полного E2E тестирования всех модулей системы
        </Typography>
      )}
    </Card>
  );
};

export default E2EIntegrationTest;
